/* eslint-disable max-lines */
/**
 * Describes interfaces for exchanging model data between two parties.
 * Client and server must implement interactions with these interfaces.
 *
 * The difference between client and server is that server can assign revisions
 * and client can't that's why server serve the role of synchronization endpoint
 * between multiple clients.
 */
import {Language} from 'yougile-lang'
import {LongId, UUID} from 'yougile-util'
import {DataObjectId, DataObjectJSON} from '../data-object'
import {BranchJSON} from '../diff'
import {EmailSettings, NotifyFrequency} from '../email-settings'
import {MarketplaceApp} from '../marketplace-app'
import {PaymentAction, PaymentStatus} from '../payment'
import {SandboxJSON} from '../sandbox'
import {BoardGuests, BoardShareType, SharingGuest} from '../sharing'
import {UserPrivileges, UserUITheme} from '../user'
import {UserAccount} from '../user-account'
import {UserStatsEvent} from './user-stats-event'

export type RegistrationType = 'google' | 'email' | 'openid'

/**
 * If client version mismath with server version then
 * server ignores start request and commit requests.
 * So that client logouts.
 */
export const dataTransmitVersion = 4

/**
 * Minor version marks for some differencies that server can handle.
 */
export const minorDataTransmissionVersion = 8

/**
 * Generic request structure, common for every request.
 */
export interface GenReq {
  reqId?: UUID
  clientType?: string
  v?: number // minorVersion
  appVersion?: string
  lang?: Language
  desktopVersion?: string
}

export interface GenError {
  result: 'error'
  error: string
  reqId?: UUID
}

/**
 * Generic response structure, common for every response.
 */
export interface GenResp {
  result: 'ok' | 'error'
  error?: string
  code?: number
  reqId?: UUID
  clientType?: string
  v?: number // minorVersion
}

/**
 * Retrieving all available data with authorization.
 */
export interface IndexReq extends GenReq {
  userId: DataObjectId
  key: string
  paging?: boolean
  page?: number
  total?: number
  companies?: DataObjectId[]
  withLazyTasks?: boolean
}

export interface IndexResp extends GenResp {
  profileId: DataObjectId
  phone?: string
  registrationType: RegistrationType | undefined
  availableCompanies: ReadonlyArray<{
    companyId: DataObjectId
    companyName: string
  }>
  pendingCompanies?: DataObjectId[]
  permissions: unknown
  paymentStatuses: {
    [companyId: string]: PaymentStatus
  }
  paymentUsersStatus: {
    [companyId: string]: {[userId: string]: boolean}
  }
  news: SandboxJSON | undefined
  data: SandboxJSON[]
  accounts: UserAccount[]
  apps: MarketplaceApp[]
  isNewsEditor: boolean
  standalone?: boolean
  devMode?: boolean
  readonly: boolean
  checksum?: number
  allowUploadExtensions?: boolean
  enabledExtensions?: {[companyId: string]: string[]}
}

export interface CompaniesReq extends GenReq {
  userId: DataObjectId
  key: string
  onlyDefault?: boolean
}

export interface CompaniesResp extends GenResp {
  companies: ReadonlyArray<{companyId: DataObjectId; companyName: string}>
}

export interface SandboxData {
  id: DataObjectId
  revision: number
  users: {[userId: string]: UserPrivileges}
  data?: {[id: string]: DataObjectJSON}
  patch?: {[id: string]: DataObjectJSON}
  branches?: BranchJSON[] // minorVer >= 3
  accounts?: UserAccount[]
}

export type CompanyRevisionType = 'far' | 'close'

export interface IndexV2Req extends GenReq {
  userId: DataObjectId
  key: string
  acceptAll: boolean
  minorVersion: number
  companies?: Array<{
    id: DataObjectId
    revision: number
    revisionType?: CompanyRevisionType
  }>
  withLazyTasks?: boolean
}

export interface IndexV2Resp extends GenResp {
  profileId: DataObjectId
  newsId: DataObjectId
  isNewsEditor: boolean
  phone?: string
  apps: MarketplaceApp[]
  standalone: boolean
  devMode: boolean
  readonly: boolean
  companies: SandboxData[]
  availableCompanies?: ReadonlyArray<{
    companyId: DataObjectId
    companyName: string
  }>
  pendingCompanies?: DataObjectId[]
  permissions: unknown
  paymentStatuses: {
    [companyId: string]: PaymentStatus
  }
  paymentUsersStatus: {
    [companyId: string]: {[userId: string]: boolean}
  }
  allowUploadExtensions?: boolean
  enabledExtensions?: {[companyId: string]: string[]}
}

export interface RevisionStateReq extends GenReq {
  userId: DataObjectId
  key: string
  companyId: DataObjectId
  revision: number
}

export interface RevisionStateResp extends GenResp {
  isActual?: boolean
}

/**
 * Starting data exchange, authorizing for data exchange.
 */
export interface StartReq extends GenReq {
  userId: DataObjectId
  key: string
  guestId: string
  guestKey: string
  version: number
  minorVer: number
  appVersion: string
  ip?: string
  userAgent?: string
  source: 'desktop' | 'web' | 'mobile'
  revisions: {[companyId: string]: number}
}

/**
 * Pull data changes from specified revision to the current state
 * (data exchange already established).
 */
export interface PullReq extends GenReq {
  companyId: DataObjectId
  revision: number
}
export interface PullResp extends GenResp {
  branch?: BranchJSON
  branches?: BranchJSON[]
}

export interface PermissionsUpRequest extends GenReq {
  companyId: DataObjectId
}

export interface ExtensionsUpRequest extends GenReq {
  companies: DataObjectId[]
}

/**
 * Committing data changes
 * (data exchange already established).
 */
export interface CommitReq extends GenReq {
  minorVer: number
  version: number
  branch?: BranchJSON
  branches?: BranchJSON[]
}
export interface CommitResp extends GenResp {
  branchId: UUID
}

/**
 * Requesting synchronizing direct messages chat hub.
 * Client must specify chatHubId, but if server requests client it should not
 * provide any chatHubId.
 */
export interface ChatReq extends GenReq {
  inviterId: DataObjectId
  inviterEmail: string
  inviterChatHubId: DataObjectId

  invitedId: DataObjectId
}

export interface ChatCheckSyncReq extends GenReq {
  userId1: DataObjectId
  userId2: DataObjectId
  profileId: DataObjectId
  chatHubId: DataObjectId
}

export interface ChatCheckSyncResp extends GenResp {
  sync?: boolean
}

/**
 * Client send a chatHubId for the chat in his profile as an approval
 * that he wishes to synchronize this chat to the chat of the other user.
 */
export interface ChatApproveReq extends GenReq {
  inviterId: DataObjectId
  inviterEmail: string
  inviterChatHubId: DataObjectId

  invitedId: DataObjectId
  invitedChatHubId: DataObjectId
}

/**
 * Check if email is already taken.
 */
export interface CheckEmailReq extends GenReq {
  email: string
}
export interface CheckEmailResp extends GenResp {
  foundUserId?: DataObjectId
  invitedUserId?: DataObjectId
}

/**
 * Invite user to some company by email.
 */
export interface InviteCompanyReq extends GenReq {
  email: string
  companyId: DataObjectId
  userId: DataObjectId
  key: string
}
export interface InviteCompanyResp extends GenResp {
  invitedId?: DataObjectId
  key?: string
}

export interface CheckInviteReq extends GenReq {
  email: string
  key: string
}

/**
 * Register a new user.
 */
export interface RegisterReq extends GenReq {
  email: string
  name: string
  password: string
  photo?: string
  phone?: string
  company?: string
  key?: string
  platform?: 'web' | 'mobile' | 'desktop'
}
export interface RegisterResp extends GenResp {
  userId?: DataObjectId
}

/**
 * Client can get a key for making other authenticated requests.
 */
export interface KeyReq extends GenReq {
  userId?: DataObjectId
  email?: string
  password: string
  smsCode?: string
  lang?: Language
  resendCode?: boolean
}
export interface KeyResp extends GenResp {
  key?: string
  userId?: DataObjectId
  nextStep?: 'phone' | 'code'
  codeSentTo?: string
  retryTimestamp?: number
}

export interface PhoneVerificationResp extends GenResp {
  userId: DataObjectId
  nextStep?: 'phone' | 'code'
  codeSentTo?: string
  retryTimestamp?: number
}

/**
 * Change privileges of the specified user.
 * User must have admin rights.
 */
export interface PrivilegesReq extends GenReq {
  targetCompanyId: DataObjectId
  targetUserId: DataObjectId
  newPrivileges: UserPrivileges
}

/**
 * Delete a user from a company. User must have admin rights in the company.
 */
export interface DeleteUserReq extends GenReq {
  targetCompanyId: DataObjectId
  targetUserId: DataObjectId
}

/**
 * Allows to create a feedback ticket from some user’s task (by convention
 * these tasks are located in user’s profile inside Feedback project).
 * So server should create a copy of this task inside Conf.feedbackCompany
 * in group Conf.feedbackInboxGroup and synchronize this task with original
 * user‘s task.
 */
export interface AddFeedbackTicketReq extends GenReq {
  companyId: DataObjectId
  taskId: DataObjectId
}

/**
 * Accounts
 */

export interface PassChangeReq extends GenReq {
  oldPassword: string
  newPassword: string
  uid: DataObjectId
}
export interface PassChangeResp extends GenResp {
  key?: string
  uid?: DataObjectId
}

/**
 * Client can provide profile information that are public-available for
 * other communicating with users who are not in his companies.
 */
export interface AccountUpReq extends GenReq {
  account: UserAccount
}

/**
 * Refresh user account activity information.
 */

/**
 * Any registered user can access other user’s public account information
 * via this request.
 */
export interface AccountListReq extends GenReq {
  userIds: DataObjectId[]
}
export interface AccountListResp extends GenResp {
  accounts?: {[userId: string]: UserAccount}
  notFound?: DataObjectId[]
}

/**
 * Subscribe to accounts changes.
 */
export interface AccountSubscribeReq extends GenReq {
  userIds: DataObjectId[]
}

/**
 * Notifies of accounts change
 */
export interface AccountChangeReq extends GenReq {
  accounts: {[userId: string]: UserAccount}
}

/**
 * Gets data needed to render email settings page
 */
export interface EmailSettingsResp extends GenResp {
  data: {
    account?: UserAccount
    settings: EmailSettings
  }
}

export interface EmailSettingsChangeReq extends GenReq {
  email: string
  key: string
  notify?: NotifyFrequency
  news?: boolean
}

export interface EmailSettingsKeyResp extends GenResp {
  key: string
}

export interface MPAppsResp extends GenResp {
  apps: MarketplaceApp[]
}

export interface MPAddReq extends GenReq {
  app: MarketplaceApp
}

export interface MPToggleReq extends GenReq {
  appId: UUID
}

export interface CompanyListReq extends GenReq {
  userId?: DataObjectId
  key?: string
}
export interface CompanyListResp extends GenResp {
  companies?: DataObjectId[]
}

export interface ApiHttpReq extends GenReq {
  userId: DataObjectId
  companyId: DataObjectId
  key: string
  uri: string
  params: unknown
  headers?: {[name: string]: string}
}

export interface ApiHttpResp extends GenResp {
  status: number
  headers: {[name: string]: string | string[]}
  body: string
}

export type LicenseTariff = 'platform' | undefined

export interface InitResp extends GenResp {
  dataUrl: string
  wsUrl: string
  uploadFileLimit: number
  standalone?: boolean
  noLicense?: boolean
  licenseExpired?: boolean
  noUsers?: boolean
  version?: string
  ip: string | undefined
  requireLogout?: boolean

  disableInviteUsers?: boolean
  disableEmailRecovery?: boolean
  allowExtensions?: boolean
  lang?: Language
  mainCompanyId?: DataObjectId
  isPlatformTariff?: boolean

  keyExpirationTimeout?: number
  internationalPhoneNumbers?: boolean
  openId?: {
    signInButtonText: string
    allowOnlyOpenId?: boolean
  }

  allowWebExtensions?: boolean
  webPlatformUrl?: string

  scalableAccountCluster?: boolean

  features?: {
    actualize?: boolean // clients can request /data/index-v2
    webDataCache?: boolean // web clients should save data to inexedDB
    companies?: boolean // endpoint /data/companies is available
    report?: boolean // endpoint /data/report is available
    payment?: boolean
    permissions?: boolean
    userEvents?: boolean // endpoint /data/user-events/...
    customRoles?: boolean
    numericAndTextStickers?: boolean
    nativeWs?: boolean
    syncFallback?: boolean
    unsplash?: boolean
    taskTemplatesSubtasks?: boolean
    autoCompleteTasks?: boolean
    idTasks?: boolean
    idTasksByProject?: boolean
    counterInColumns?: boolean
    isLastVersionIconsFeasible?: boolean
    mirroredTasks?: boolean
    voting?: boolean
    pendingRegistrations?: boolean
    ganttAutoPlanning?: boolean
    ganttLag?: boolean
    dragFromReports?: boolean
    myTasksLists?: boolean
  }
  analyticPlatformUrl?: string
  analyticPlatformToken?: string
  supportSyncRoleUsers?: boolean
}

export interface TimeResp extends GenResp {
  time: number
  version?: number
  startOfDay: number
  date: string // YYYY-MM-DD
}

export interface AddCompanyReq extends GenReq {
  userId: DataObjectId
  key: string
  companyName: string
}

export interface AddCompanyResp extends GenResp {
  companyId: DataObjectId
}

export interface RecoveryReq extends GenReq {
  email: string
  key: string
  password: string
}

export interface RecoveryResp extends GenResp {
  userId: DataObjectId
}

export interface UserCardIdReq extends GenReq {
  cardId: string
  userId?: DataObjectId
}

export interface UserCardIdResp extends GenResp {
  cardId: string
}

export interface UserCardEventReq extends GenReq {
  cardId: string
  event: UserStatsEvent
  ref?: string
}

export interface ReloadReq extends GenReq {
  companyId?: DataObjectId
}

export interface ReportReq extends GenReq {
  message: string
  version?: string
  userId?: DataObjectId
}

export interface KickOutRequest extends GenReq {
  companyId: DataObjectId
}

export interface PermissionsGetRequest extends GenReq {
  userId: DataObjectId
  key: string
  companyId: DataObjectId
}
export interface PermissionsGetResponse extends GenResp {
  companyId: DataObjectId
  permissions: undefined
}

export interface PermissionsGetAllRequest extends GenReq {
  userId: DataObjectId
  key: string
}
export interface PermissionsGetAllResponse extends GenResp {
  permissions: unknown
}

/**
 * Payments Service
 */
export interface PaymentUpRequest extends GenReq {
  companyId: DataObjectId
}
export interface PaymentGetRequest extends GenReq {
  userId: DataObjectId
  key: string
  companyId: DataObjectId
}
export interface PaymentGetResponse extends GenResp {
  companyId: DataObjectId
  users: {[userId: string]: boolean}
  status: PaymentStatus
}
export interface PaymentGetAllRequest extends GenReq {
  userId: DataObjectId
  key: string
}
export interface PaymentGetAllResponse extends GenResp {
  statuses: {
    [companyId: string]: PaymentStatus
  }
  users: {
    [companyId: string]: {[userId: string]: boolean}
  }
}

export interface PaymentInvoiceReq extends GenReq {
  userId: DataObjectId
  key: string
  companyId: DataObjectId
  action: PaymentAction
  email: string
  phone: string
  fullName?: string
  address?: string
  tin?: string
  trrc?: string
  accNum?: string
  corrAccNum?: string
  rcbic?: string
}

export interface PaymentInvoiceResp extends GenResp {
  invoiceId: LongId
  publicId: string
}

export interface CompanyLinkDataResp extends GenResp {
  companyId: DataObjectId
  companyName: string
  user?: {
    name: string
    avatar: string
    email: string
    isInCompany: boolean
  }
}

export interface SharedBoardsCredResp {
  companyId: DataObjectId
  boardId: DataObjectId
  guestId?: string
  guestKey?: string
  shareType: BoardShareType
  userId: DataObjectId
  key?: string
  background: string
  lang: Language
  theme: UserUITheme
  guests: BoardGuests
  guest?: SharingGuest
}
